import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import axios from 'axios'
import FinanceDetails from '../../shared/finance/FinanceDetails'
import FileList from '../../shared/file-upload/FileList'
import { buildFileDownloadUrl, getApiBaseUrl } from '../../utils/apiConfig'
import { getStorageValues } from '../../shared/CommonFunctions'
import { useMasterData } from '../../utils/useMasterData'
import { calculateReducingRateSummary, parseFinanceValues, validateFinanceValues } from '../../shared/finance/loanCalculator'

const INITIAL_FORM_STATE = {
  SalesId: '',
  SalesNumber: '',
  CustomerName: '',
  MobileNo: '',
  EmailId: '',
  CustomerAddress: '',
  CustomerId: '',
  VehicleId: '',
  VehicleLabel: '',
  VehicleCost: '',
  ChassisNo: '',
  EngineNo: '',
  FinancePartnerId: '',
  DownPayment: '',
  EMITenure: '',
  ProcessingFees: '',
  InterestRate: '',
  EMIAmount: '',
  LoanApplicationNo: ''
}

function AddFinanceRegister({ sendDataToParent, onLoad, onLoanSummary, onLoanSummaryApply }) {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE)
  const [salesOptions, setSalesOptions] = useState([])
  const [selectedSale, setSelectedSale] = useState(null)
  const [financePartners, setFinancePartners] = useState([])
  const [financePartnersError, setFinancePartnersError] = useState('')
  const [selectedFinancePartnerId, setSelectedFinancePartnerId] = useState(null)
  const [loanSummaryError, setLoanSummaryError] = useState('')
  const [customerFiles, setCustomerFiles] = useState([])
  const { items: interestRateItems, loading: interestRateLoading } = useMasterData('interestRates')

  const interestRateOptions = useMemo(() => {
    return (interestRateItems || []).map((item) => ({
      value: item.ValueName ?? '',
      label: item.DisplayName || item.ValueName || '',
      id: item.MasterValueId
    }))
  }, [interestRateItems])

  const selectedInterestRate = useMemo(() => {
    if (!formData.InterestRate) return null
    const valueStr = formData.InterestRate.toString()
    return interestRateOptions.find((opt) => opt.value?.toString() === valueStr) || null
  }, [formData.InterestRate, interestRateOptions])

  const updateFieldValue = useCallback((name, value, options = {}) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value }
      if (!options.skipParent) {
        sendDataToParent((prevState) => ({ ...prevState, [name]: value }))
      }
      return next
    })
  }, [sendDataToParent])

  const fetchSalesForDropdown = useCallback(async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/financeregister/salesfordropdown`, {
        params: { UserId: getStorageValues('userId') }
      })
      const rows = response?.data?.data || []
      const options = rows.map((row) => ({
        value: row.SalesRegisterId,
        label: `${row.SalesNumber}${row.CustomerName ? ` (${row.CustomerName})` : ''}`,
        salesNumber: row.SalesNumber,
        customerName: row.CustomerMasterName || row.CustomerName || '',
        emailId: row.CustomerMasterEmail || row.EmailId || '',
        mobileNo: row.CustomerMasterMobile || row.MobileNumber || '',
        customerAddress: row.CustomerAddress || '',
        customerId: row.CustomerId || '',
        vehicleId: row.VehicleId,
        vehicleLabel: row.VehicleLabel || '',
        vehicleCost: row.VehicleCost,
        chassisNo: row.ChassisNo || '',
        engineNo: row.EngineNo || '',
        downPayment: row.DownPayment ?? '',
        emiTenure: row.EMITenure ?? '',
        processingFees: row.ProcessingFees ?? '',
        interestRate: row.InterestRate ?? '',
        emiAmount: row.EMIAmount ?? ''
      }))
      setSalesOptions(options)
    } catch (error) {
      console.log(error)
      setSalesOptions([])
    }
  }, [])

  const fetchFinancePartners = useCallback(async () => {
    try {
      setFinancePartnersError('')
      const response = await axios.get(`${getApiBaseUrl()}/financepartner/getAll`, {
        params: { UserId: getStorageValues('userId') }
      })
      const payload = response?.data?.data
      const rows = Array.isArray(payload?.[0]) ? payload[0] : Array.isArray(payload) ? payload : []
      setFinancePartners(rows)
    } catch (error) {
      setFinancePartners([])
      setFinancePartnersError('Unable to load finance partners.')
    }
  }, [])

  const fetchCustomerDocuments = useCallback(async (customerId) => {
    if (!customerId) {
      setCustomerFiles([])
      return
    }
    try {
      const response = await axios.post(`${getApiBaseUrl()}/file/list`, {
        entityId: customerId,
        entityType: 'customer'
      })
      if (response.status === 200) {
        setCustomerFiles(response.data.data || [])
      }
    } catch (error) {
      console.log(error)
      setCustomerFiles([])
    }
  }, [])

  const handleSalesChange = (option) => {
    setSelectedSale(option)
    if (!option) {
      setFormData(INITIAL_FORM_STATE)
      sendDataToParent((prev) => ({ ...prev, ...INITIAL_FORM_STATE }))
      setSelectedFinancePartnerId(null)
      return
    }

    const nextState = {
      ...formData,
      SalesId: option.value,
      SalesNumber: option.salesNumber || '',
      CustomerName: option.customerName || '',
      MobileNo: option.mobileNo || '',
      EmailId: option.emailId || '',
      CustomerAddress: option.customerAddress || '',
      CustomerId: option.customerId || '',
      VehicleId: option.vehicleId || '',
      VehicleLabel: option.vehicleLabel || '',
      VehicleCost: option.vehicleCost ?? '',
      ChassisNo: option.chassisNo || '',
      EngineNo: option.engineNo || '',
      DownPayment: option.downPayment ?? '',
      EMITenure: option.emiTenure ?? '',
      ProcessingFees: option.processingFees ?? '',
      InterestRate: option.interestRate !== null && option.interestRate !== undefined
        ? option.interestRate.toString()
        : '',
      EMIAmount: option.emiAmount ?? ''
    }

    setFormData(nextState)
    sendDataToParent((prev) => ({ ...prev, ...nextState }))
    setSelectedFinancePartnerId(null)
  }

  const handleFinancePartnerClick = (partner) => {
    if (!partner) return
    const rate = partner.InterestRate ?? ''
    const downPayment = partner.MinimumDownPayment ?? ''
    updateFieldValue('InterestRate', rate === null ? '' : rate.toString())
    updateFieldValue('DownPayment', downPayment === null ? '' : downPayment.toString())
    updateFieldValue('EMIAmount', '')
    updateFieldValue('FinancePartnerId', partner.FinancePartnerId ?? '')
    const key = partner.FinancePartnerId ?? partner.FinancePartnerName ?? null
    setSelectedFinancePartnerId(key)
  }

  const parsedFinanceValues = useMemo(() => parseFinanceValues(formData), [
    formData.VehicleCost,
    formData.DownPayment,
    formData.EMITenure,
    formData.ProcessingFees,
    formData.InterestRate
  ])

  const calculateLoanSummary = () => {
    validateFinanceValues(parsedFinanceValues)
    return calculateReducingRateSummary(parsedFinanceValues)
  }

  const handleLoanSummaryClick = () => {
    try {
      const summary = calculateLoanSummary()
      setLoanSummaryError('')
      if (typeof onLoanSummary === 'function') {
        onLoanSummary(summary)
      }
    } catch (err) {
      setLoanSummaryError(err.message || 'Unable to calculate loan summary.')
    }
  }

  const handlePreviewDocument = useCallback((file) => {
    if (!file || !file.FileLibraryId) return
    const url = buildFileDownloadUrl(file.FileLibraryId)
    window.open(url, '_blank')
  }, [])

  useEffect(() => {
    onLoad?.(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (typeof onLoanSummaryApply === 'function') {
      onLoanSummaryApply((summary) => {
        updateFieldValue('EMIAmount', summary?.monthlyEmi ? String(summary.monthlyEmi) : '')
      })
    }
  }, [onLoanSummaryApply, updateFieldValue])

  useEffect(() => {
    fetchSalesForDropdown()
    fetchFinancePartners()
  }, [fetchSalesForDropdown, fetchFinancePartners])

  useEffect(() => {
    fetchCustomerDocuments(formData.CustomerId)
  }, [fetchCustomerDocuments, formData.CustomerId])

  const vehicleOption = useMemo(() => {
    if (!formData.VehicleId) return null
    return { value: formData.VehicleId, label: formData.VehicleLabel || 'Vehicle' }
  }, [formData.VehicleId, formData.VehicleLabel])

  return (
    <>
      <div className="row">
        <div className="col-lg-12 col-sm-12 col-12 mb-3">
          <label className="form-label">Customer Sales<span style={{ color: 'red' }}>*</span></label>
          <Select
            value={selectedSale}
            onChange={handleSalesChange}
            options={salesOptions}
            placeholder="Select sales number"
            isClearable
          />
        </div>

        <div className="col-12">
          <div className="border rounded p-3 mb-3">
            <h6 className="mb-3">Customer Details</h6>
            <div className="row">
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Customer Name</label>
                <input type="text" className="form-control" value={formData.CustomerName} disabled />
              </div>
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Email Address</label>
                <input type="text" className="form-control" value={formData.EmailId} disabled />
              </div>
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Phone Number</label>
                <input type="text" className="form-control" value={formData.MobileNo} disabled />
              </div>
              <div className="col-lg-12 col-sm-12 col-12 mb-3">
                <label className="form-label">Address</label>
                <input type="text" className="form-control" value={formData.CustomerAddress} disabled />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="border rounded p-3 mb-3">
            <h6 className="mb-3">Documents</h6>
            <FileList
              files={customerFiles}
              onDownload={handlePreviewDocument}
              showRemove={false}
              showDownload={true}
              downloadLabel="Preview"
            />
          </div>
        </div>

        <div className="col-12">
          <div className="border rounded p-3 mb-3">
            <h6 className="mb-3">Vehicle Details</h6>
            <div className="row">
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Vehicle</label>
                <Select
                  value={vehicleOption}
                  options={vehicleOption ? [vehicleOption] : []}
                  isDisabled
                />
              </div>
              <div className="col-lg-2 col-sm-6 col-12 mb-3">
                <label className="form-label">Vehicle Cost</label>
                <input type="text" className="form-control" value={formData.VehicleCost} disabled />
              </div>
              <div className="col-lg-3 col-sm-6 col-12 mb-3">
                <label className="form-label">Chassis No</label>
                <input type="text" className="form-control" value={formData.ChassisNo} disabled />
              </div>
              <div className="col-lg-3 col-sm-6 col-12 mb-3">
                <label className="form-label">Engine No</label>
                <input type="text" className="form-control" value={formData.EngineNo} disabled />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <FinanceDetails
            financePartners={financePartners}
            financePartnersError={financePartnersError}
            selectedFinancePartnerId={selectedFinancePartnerId}
            onFinancePartnerClick={handleFinancePartnerClick}
            downPaymentValue={formData.DownPayment}
            emiTenureValue={formData.EMITenure}
            processingFeesValue={formData.ProcessingFees}
            interestRateValue={formData.InterestRate}
            emiAmountValue={formData.EMIAmount}
            onDownPaymentChange={(e) => updateFieldValue('DownPayment', e.currentTarget.value)}
            onEmiTenureChange={(e) => updateFieldValue('EMITenure', e.currentTarget.value)}
            onProcessingFeesChange={(e) => updateFieldValue('ProcessingFees', e.currentTarget.value)}
            onInterestRateChange={(option) => updateFieldValue('InterestRate', option ? option.value : '')}
            onEmiAmountChange={(e) => updateFieldValue('EMIAmount', e.currentTarget.value)}
            interestRateOptions={interestRateOptions}
            interestRateLoading={interestRateLoading}
            selectedInterestRate={selectedInterestRate}
            onCalculateEmi={handleLoanSummaryClick}
            loanSummaryError={loanSummaryError}
          />
        </div>

        <div className="col-lg-4 col-sm-6 col-12 mb-3">
          <label className="form-label">Loan Application No</label>
          <input
            type="text"
            className="form-control"
            name="LoanApplicationNo"
            value={formData.LoanApplicationNo}
            onChange={(e) => updateFieldValue('LoanApplicationNo', e.currentTarget.value)}
          />
        </div>
      </div>
    </>
  )
}

export default AddFinanceRegister